import { forwardRef, useEffect, useImperativeHandle, useState } from "react";
import {
  COLOR_DARK,
  COLOR_DIVISOR,
  FONT,
  REGEX_VALID_DECIMAL
} from "../defaults";
import { InputBoxRemainder, levelMayaWriteDivision } from "../strings";

/*
This component implements a input box into which the dividend, divisor,
and optionally the remainder can be entered.
If the input is wrong, it is reset and handleIncorrect is called.
If it is correct, handleCorrect is called.
*/
const InputBoxDec = forwardRef((
  {handleCorrect,
  handleIncorrect,
  handleNoInput,
  dividend,
  divisor,
  remainder,
  showMessage,
  disableInput},
  ref
) => {
  const [inputQuotient, setInputQuotient] = useState("");
  const [inputRemainder, setInputRemainder] = useState("");
  const [inputDividend, setInputDividend] = useState("");

  // These Booleans store correctness of inputs, solely for the purpose of deleting wrong inputs.
  const [isQuotientCorrect, setIsQuotientCorrect] = useState(true);
  const [isRemainderCorrect, setIsRemainderCorrect] = useState(true);
  const [isDividendCorrect, setIsDividendCorrect] = useState(true);

  // Whether or not all input fields were filled.
  const [allFilled, setAllFilled] = useState(true);


  const handleQuotientChange = (e) => {
    e.target.value = e.target.value.replace(/\s/g, ''); // LLM-generated. Disallow white-space input.
    setInputQuotient(e.target.value);
  };

  const handleRemainderChange = (e) => {
    e.target.value = e.target.value.replace(/\s/g, ''); // LLM-generated. Disallow white-space input.
    setInputRemainder(e.target.value);
  };

  const handleDividendChange = (e) => {
    e.target.value = e.target.value.replace(/\s/g, ''); // LLM-generated. Disallow white-space input.
    setInputDividend(e.target.value);
  };

   /**
    * Handles deleting of input fields after closing checkAnswer popup.
    */
  const manageInputsAfterCheck = () => {
    if (allFilled) { // Only delete inputs if all fields are filled.
      if (isQuotientCorrect && isRemainderCorrect && isDividendCorrect) { // Everything correct.
        setInputQuotient("");
        setInputRemainder("");
        setInputDividend("");
      }
      else { // Some inputs wrong.
        if (!isQuotientCorrect) setInputQuotient("");
        if (!isRemainderCorrect) setInputRemainder("");
        if (!isDividendCorrect) setInputDividend("");
      }
    }
    setIsQuotientCorrect(true); // Important to set all variables to true, to make sure everything is properly initialized for the next exercise.
    setIsRemainderCorrect(true);
    setIsDividendCorrect(true);
    setAllFilled(true);
  };

  // Only executed when showMessage is set to false, i.e. when message popup is closed after being displayed.
  useEffect(() => {
    if (!showMessage) {
      manageInputsAfterCheck();
    }
  }, [showMessage]);

  const checkInput = () => {
    if (inputQuotient === "" || inputDividend === "" || (remainder > 0 && inputRemainder === "")) { // Not all fields filled.
        handleNoInput();
        setAllFilled(false);
      }
      else if ( // Input was correct.
      inputQuotient === ((dividend - remainder) / divisor).toString() && inputDividend === dividend.toString() && (remainder > 0 ? inputRemainder === remainder.toString() : true)) {
        handleCorrect();
        setIsQuotientCorrect(true);
        setIsRemainderCorrect(true);
        setIsDividendCorrect(true);
      } 
      else { // Input was wrong. All input fields with wrong inputs are reset.
        handleIncorrect();
        if (inputQuotient !== ((dividend - remainder) / divisor).toString()) {
          setIsQuotientCorrect(false);
        }
        if (inputRemainder !== remainder.toString()) {
          setIsRemainderCorrect(false);
        }
        if (inputDividend !== dividend.toString()) {
          setIsDividendCorrect(false);
        }
    }
  };

  useImperativeHandle(ref, () => ({
    checkInput,
  }));

  const inputStyle = {
    fontFamily: FONT,
    fontSize: "2.3dvh",
  };

  return (
    <div
      style={{
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      justifyContent: "center",
      fontFamily: FONT,
      fontSize: inputStyle.fontSize,
      textShadow:"0.3svh 0.3svh 0.6svh rgba(0, 0, 0, 0.5)",
      backgroundColor: COLOR_DARK,
      padding: "2.5dvh",
      borderRadius: "0.25em",
      boxShadow: "0.3dvh 0.3dvh 0.6dvh rgba(0, 0, 0, 0.5)",
      border: "0.2dvh solid black"
      }}
    >
      <div style={{display: "flex", marginBottom: "2dvh", justifyContent: "left"}}>
          <span style={{fontSize: "2dvh"}}>{levelMayaWriteDivision}</span>
      </div>
      <div style={{display: "flex", flexDirection: "row", alignItems: "center"}}>
          <span style={{fontFamily: FONT}}>
              <input
                  type="text"
                  inputMode="numeric"
                  value={inputDividend}
                  placeholder=""
                  onChange={handleDividendChange}
                  className="input-field"
                  pattern={REGEX_VALID_DECIMAL}
                  style={inputStyle}
                  disabled={disableInput}
              />
          </span>
          <span>&nbsp;&nbsp;:&nbsp;&nbsp;</span>
          <span style={{ color: COLOR_DIVISOR }}>{divisor}</span>
          <span>&nbsp;&nbsp;=&nbsp;&nbsp;</span>
          <span>
            <input 
                type="text"
                inputMode="numeric"
                value={inputQuotient}
                placeholder=""
                onChange={handleQuotientChange}
                className="input-field"
                pattern={REGEX_VALID_DECIMAL}
                style={inputStyle}
                disabled={disableInput}
            />
          </span>
          {remainder > 0 && (
          <span>
              &nbsp;&nbsp;&nbsp;{InputBoxRemainder}&nbsp;&nbsp;&nbsp;
              <input
                  type="text"
                  inputMode="numeric"
                  value={inputRemainder}
                  placeholder=""
                  onChange={handleRemainderChange}
                  className="input-field"
                  pattern={REGEX_VALID_DECIMAL}
                  style={inputStyle}
                  disabled={disableInput}
          />
          </span>
          )}
      </div>
    </div>
  );
});

export default InputBoxDec;
